import { ScenarioConfig, AnimationPattern } from '../animation/type';

/**
 * 애니메이션 배열에서 일관된 세트를 선택하는 함수
 * 실제로 존재하는 변형들 중에서만 랜덤하게 선택
 * 예: ["listening01_a", "listening01_b"] → 실제 존재하는 01, 03 중에서 선택
 * @param animations 애니메이션 배열
 * @param maxVariantCount 사용 가능한 변형의 최대 개수 (사용하지 않음)
 * @returns 일관된 세트로 선택된 애니메이션 배열
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function selectConsistentAnimationSet(animations: string[], maxVariantCount: number): string[] {
  if (animations.length === 0) {
    return animations;
  }

  // 실제로 존재하는 변형 번호들을 추출
  const existingVariants = new Set<string>();
  animations.forEach(animation => {
    const match = animation.match(/(\d+)/);
    if (match) {
      existingVariants.add(match[1]);
    }
  });

  const variants = Array.from(existingVariants);
  if (variants.length === 0) {
    return animations;
  }

  // 실제 존재하는 변형들 중에서 랜덤하게 하나 선택
  const selectedVariant = variants[Math.floor(Math.random() * variants.length)];

  const result = animations.map(animation => {
    return animation.replace(/(\d+)/g, (match) => {
      return selectedVariant.padStart(match.length, '0');
    });
  });

  return result;
}

/**
 * 애니메이션 배열에서 각각 독립적인 변형을 선택하는 함수
 * 실제로 존재하는 변형들 중에서만 랜덤하게 선택
 * @param animations 애니메이션 배열
 * @param minVariant 최소 변형 번호 (사용하지 않음)
 * @param maxVariant 최대 변형 번호 (사용하지 않음)
 * @returns 원본 애니메이션 배열 (실제 존재하는 애니메이션만)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function selectIndependentAnimationVariants(animations: string[], minVariant: number, maxVariant: number): string[] {
  if (animations.length === 0) {
    return animations;
  }

  // 실제로 존재하는 변형 번호들을 추출
  const existingVariants = new Set<string>();
  animations.forEach(animation => {
    const match = animation.match(/(\d+)/);
    if (match) {
      existingVariants.add(match[1]);
    }
  });

  const variants = Array.from(existingVariants);
  if (variants.length === 0) {
    return animations;
  }

  // 각 애니메이션마다 실제 존재하는 변형들 중에서 독립적으로 선택
  const result = animations.map(animation => {
    const selectedVariant = variants[Math.floor(Math.random() * variants.length)];
    return animation.replace(/(\d+)/g, (match) => {
      return selectedVariant.padStart(match.length, '0');
    });
  });

  console.log(`🎯 Selected independent variants from available [${variants.join(', ')}]:`, result);
  return result;
}

/**
 * ScenarioConfigs에서 애니메이션 배열만 추출하는 헬퍼 함수
 * @param scenarioConfigs ScenarioConfigs 객체
 * @returns Record<string, string[]> 형식의 애니메이션 맵
 */
export function extractAnimationsFromScenarioConfigs(scenarioConfigs: import('../animation/type').ScenarioConfigs<string>): Record<string, string[]> {
  
  const animations: Record<string, string[]> = {};
  for (const [key, config] of Object.entries(scenarioConfigs)) {
    animations[key] = config.animations;
  }
  
  return animations;
}

/**
 * 시나리오 설정 생성을 위한 헬퍼 함수들
 */
export const ScenarioBuilders = {
  /**
   * 단일 애니메이션 시나리오 (한 번 실행)
   */
  single(animation: string, options?: { blendDuration?: number; description?: string; nextScenario?: string }): ScenarioConfig {
    return {
      animations: [animation],
      playMode: 'once',
      selectionMode: 'none',
      blendDuration: options?.blendDuration || 0.2,
      description: options?.description,
      animationPattern: 'single',
      nextScenario: options?.nextScenario
    };
  },

  /**
   * 순차적 페어 애니메이션 시나리오 (A -> B, B 반복)
   */
  pairedSequence(
    animationA: string,
    animationB: string,
    options?: {
      selectionMode?: 'none' | 'consistent_set';
      blendDuration?: number;
      description?: string;
      nextScenario?: string;
    }
  ): ScenarioConfig {
    return {
      animations: [animationA, animationB],
      playMode: 'last_loop',
      selectionMode: options?.selectionMode || 'consistent_set',
      blendDuration: options?.blendDuration || 0.2,
      description: options?.description,
      animationPattern: 'paired_sequence',
      nextScenario: options?.nextScenario,
      options: {
        isLastAnimationLoop: true
      }
    };
  },

  /**
   * 다수 랜덤 애니메이션 시나리오 (랜덤 무한 반복)
   */
  multipleRandom(
    animations: string[],
    options?: { blendDuration?: number; description?: string; nextScenario?: string }
  ): ScenarioConfig {
    return {
      animations,
      playMode: 'random_loop',
      selectionMode: 'none',
      blendDuration: options?.blendDuration || 0.2,
      description: options?.description,
      animationPattern: 'multiple_random',
      nextScenario: options?.nextScenario,
      options: {
        isRandom: true
      }
    };
  },

  /**
   * 독립적인 다수 애니메이션 시나리오 (한 번 실행, 각각 독립적 변형)
   */
  multipleIndependent(
    animations: string[],
    options?: { blendDuration?: number; description?: string; nextScenario?: string }
  ): ScenarioConfig {
    return {
      animations,
      playMode: 'once',
      selectionMode: 'independent_variants',
      blendDuration: options?.blendDuration || 0.2,
      description: options?.description,
      animationPattern: 'multiple_independent',
      nextScenario: options?.nextScenario
    };
  }
};

/**
 * 리소스에서 베이스 애니메이션 이름을 분석하여 실제 존재하는 애니메이션들을 찾는 함수
 */
export function analyzeAnimationResources(
  baseName: string,
  resources: { name: string }[]
): {
  pattern: AnimationPattern;
  animations: string[];
  maxVariants: number;
  hasSequentialPair: boolean;
} {
  
  

  // 베이스 이름과 일치하는 모든 애니메이션 찾기
  const matchingAnimations = resources
    .map(r => r.name)
    .filter(name => {
      // 정확한 베이스 이름 매칭 (베이스 이름 + 숫자 또는 정확히 일치)
      const exactMatch = name === baseName;
      const numberedMatch = name.match(new RegExp(`^${baseName}\\d+(_[ab])?$`));
      const result = exactMatch || numberedMatch !== null;
      return result;
    })
    .sort();

  

  if (matchingAnimations.length === 0) {
    console.warn(`⚠️ No matching animations found for "${baseName}"`);
    return {
      pattern: 'single',
      animations: [baseName],
      maxVariants: 1,
      hasSequentialPair: false
    };
  }

  // 단일 애니메이션인 경우
  if (matchingAnimations.length === 1) {
    return {
      pattern: 'single',
      animations: matchingAnimations,
      maxVariants: 1,
      hasSequentialPair: false
    };
  }

  // 순차적 페어 패턴 확인 (예: input_listening01_a, input_listening01_b)
  const pairPattern = matchingAnimations.filter(name =>
    name.match(new RegExp(`^${baseName}\\d+_[ab]$`))
  );

  if (pairPattern.length >= 2) {
    // 숫자별로 그룹핑해서 a, b 쌍이 있는지 확인
    const numberGroups: Record<string, string[]> = {};
    pairPattern.forEach(name => {
      const match = name.match(new RegExp(`^${baseName}(\\d+)_([ab])$`));
      if (match) {
        const [, number, suffix] = match;
        if (!numberGroups[number]) numberGroups[number] = [];
        numberGroups[number].push(suffix);
      }
    });

    // 완전한 a, b 쌍이 있는 그룹 찾기
    const completePairs = Object.entries(numberGroups)
      .filter(([, suffixes]) => suffixes.includes('a') && suffixes.includes('b'))
      .map(([number]) => number);

    if (completePairs.length > 0) {
      // 첫 번째 완전한 쌍 사용
      const firstPair = completePairs[0];
      return {
        pattern: 'paired_sequence',
        animations: [`${baseName}${firstPair.padStart(2, '0')}_a`, `${baseName}${firstPair.padStart(2, '0')}_b`],
        maxVariants: completePairs.length,
        hasSequentialPair: true
      };
    }
  }

  // 번호가 있는 다수 애니메이션 확인 (예: speaking01, speaking02, speaking03)
  const numberedPattern = matchingAnimations.filter(name =>
    name.match(new RegExp(`^${baseName}\\d+$`))
  );

  if (numberedPattern.length > 1) {
    return {
      pattern: 'multiple_random',
      animations: numberedPattern,
      maxVariants: numberedPattern.length,
      hasSequentialPair: false
    };
  }

  // 서로 다른 베이스를 가진 애니메이션들인지 확인
  const differentBases = matchingAnimations.some(name =>
    !name.startsWith(baseName) || name.includes('_') && !name.match(/\d+_[ab]$/)
  );

  if (differentBases) {
    return {
      pattern: 'multiple_independent',
      animations: matchingAnimations,
      maxVariants: matchingAnimations.length,
      hasSequentialPair: false
    };
  }

  // 기본값: 모든 애니메이션을 랜덤 그룹으로 처리
  return {
    pattern: 'multiple_random',
    animations: matchingAnimations,
    maxVariants: matchingAnimations.length,
    hasSequentialPair: false
  };
}

/**
 * JSON 호환 스키마에서 ScenarioConfig를 생성하는 함수
 */
export interface JsonScenarioSchema {
  animation?: string;           // 단일 애니메이션 또는 베이스 이름
  animations?: string[];        // 복수 애니메이션
  mode?: 'once' | 'loop' | 'random' | 'last_loop'; // 간단한 모드 이름
  description?: string;
  blendDuration?: number;
}

/**
 * 스마트한 시나리오 빌더 - 베이스 이름만으로 자동 구성
 */
export const SmartScenarioBuilders = {
  /**
   * 베이스 이름을 분석해서 자동으로 적절한 시나리오 생성
   */
  auto(
    baseName: string,
    resources: { name: string }[],
    options?: {
      preferredMode?: 'once' | 'loop' | 'last_loop' | 'random';
      blendDuration?: number;
      description?: string;
      nextScenario?: string;
    }
  ): ScenarioConfig {
    
    const analysis = analyzeAnimationResources(baseName, resources);
    

    const defaultOptions = {
      blendDuration: options?.blendDuration || 0.2,
      description: options?.description || `${baseName} 애니메이션 (자동 생성)`,
      nextScenario: options?.nextScenario
    };

    // 사용자가 선호하는 모드가 있으면 우선 적용
    if (options?.preferredMode) {
      switch (options.preferredMode) {
        case 'once':
          if (analysis.animations.length > 1) {
            // 다수 애니메이션은 전부 등록하고, 재생 시점에 1개를 랜덤 선택 (base-player.playScenario)
            // 종전에는 여기(설정 로드 시점)에서 1개를 뽑아 세션 내내 같은 클립으로 고정됐음
            return {
              animations: analysis.animations,
              playMode: 'once',
              selectionMode: 'none',
              blendDuration: defaultOptions.blendDuration,
              description: defaultOptions.description,
              animationPattern: 'multiple_random',
              nextScenario: defaultOptions.nextScenario
            };
          } else {
            return ScenarioBuilders.single(analysis.animations[0], defaultOptions);
          }
        case 'last_loop':
          if (analysis.pattern === 'paired_sequence') {
            return ScenarioBuilders.pairedSequence(analysis.animations[0], analysis.animations[1], defaultOptions);
          } else if (analysis.animations.length > 1) {
            // 다수 애니메이션이 있으면 랜덤하게 하나만 선택해서 무한 반복
            const selectedAnimation = analysis.animations[Math.floor(Math.random() * analysis.animations.length)];
            return {
              animations: [selectedAnimation],
              playMode: 'last_loop',
              selectionMode: 'none',
              blendDuration: defaultOptions.blendDuration,
              description: defaultOptions.description,
              animationPattern: 'single',
              nextScenario: defaultOptions.nextScenario,
              options: {
                isLastAnimationLoop: true
              }
            };
          } else {
            // 단일 애니메이션이면 그냥 한 번만 실행
            return ScenarioBuilders.single(analysis.animations[0], defaultOptions);
          }
          break;
        case 'random':
        case 'loop':
          if (analysis.animations.length > 1) {
            return ScenarioBuilders.multipleRandom(analysis.animations, defaultOptions);
          } else {
            // 단일 애니메이션이면 한 번만 실행
            return ScenarioBuilders.single(analysis.animations[0], defaultOptions);
          }
          break;
      }
    }

    // 자동 패턴 매칭
    switch (analysis.pattern) {
      case 'single':
        return ScenarioBuilders.single(analysis.animations[0], defaultOptions);

      case 'paired_sequence':
        return ScenarioBuilders.pairedSequence(
          analysis.animations[0],
          analysis.animations[1],
          defaultOptions
        );

      case 'multiple_random':
        return ScenarioBuilders.multipleRandom(analysis.animations, defaultOptions);

      case 'multiple_independent':
        return ScenarioBuilders.multipleIndependent(analysis.animations, defaultOptions);

      default:
        return ScenarioBuilders.single(analysis.animations[0], defaultOptions);
    }
  }
};